import { useState } from 'react';
import { Alert, Button, Group, Modal, Select, Tooltip } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { useCreateStockCount, useSites } from '../api';

/** "New count" entry point for the stock-count register (BUILD_SPEC.md §8.6:
 * "create a count"). A count only needs a site to start — lines are added once
 * it's started, on the detail screen. */
export function CreateCountButton() {
  const canOperate = useCanOperate();
  const [opened, setOpened] = useState(false);
  const [siteId, setSiteId] = useState<string | null>(null);
  const sitesQuery = useSites();
  const createCount = useCreateStockCount();
  const navigate = useNavigate();

  function close() {
    setOpened(false);
    setSiteId(null);
    createCount.reset();
  }

  function handleCreate() {
    if (!siteId) return;
    createCount.mutate(
      { siteId },
      {
        onSuccess: (count) => {
          close();
          void navigate({ to: '/silo-counts/counts/$countId', params: { countId: count.id } });
        },
      },
    );
  }

  const sites = sitesQuery.data?.items ?? [];

  return (
    <>
      <Button leftSection={<IconPlus size={16} />} onClick={() => setOpened(true)}>
        New count
      </Button>
      <Modal opened={opened} onClose={close} title="Start a new stock count">
        <Select
          label="Site"
          placeholder="Choose a site"
          data={sites.map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))}
          value={siteId}
          onChange={setSiteId}
          disabled={sitesQuery.isLoading}
          mb="md"
        />
        {createCount.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} mb="md">
            {createCount.error instanceof ApiRequestError ? createCount.error.message : createCount.error?.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button loading={createCount.isPending} disabled={!canOperate || !siteId} onClick={handleCreate}>
                Create
              </Button>
            </span>
          </Tooltip>
        </Group>
      </Modal>
    </>
  );
}
